import {observer} from 'mobx-react-lite'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import useResizeObserver from 'use-resize-observer'
import {Hub} from 'yougile-model'
import {Items} from '@api/model/items'
import {boardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {isCollapsedHeader} from '@components/board/general-board-settings-popup/collapsed-header-checkbox'
import {IconButton} from '@components/common/base/buttons'
import {useDnDStore} from '@components/draggable'
import {BoardTab, NewBoardTab} from '@components/upper-panel/board-tab'
import {
  BoardTabsContext,
  BoardTabsStore,
  useBoardTabsStore
} from '@components/upper-panel/board-tabs-store'
import {useBoardTabsButtonScroll} from '@components/upper-panel/use-board-tabs-button-scroll'
import {
  fadeThreshold,
  useRefreshFade
} from '@components/upper-panel/use-refresh-fade'
import {
  useGlobalKeyDown,
  useLocationUpdateEffect,
  useSyncEvent
} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'

const TwBoardTabs = tw.div<{$collapsed?: boolean}>`
  w-full
  max-w-full
  flex
  items-center
  gap-2
  ${p => (p.$collapsed ? 'pt-8' : 'pt-8')}
`

// 76px = 64px TwControls max width + 12px right padding
const TwTabs = tw.div<{
  $fadeLeft?: boolean
  $fadeRight?: boolean
  $collapsed?: boolean
}>`
  h-32
  relative
  flex-initial
  isolate
  overflow-hidden
  ${p =>
    p.$collapsed
      ? '[max-width:calc(100%-108px)]'
      : '[max-width:calc(100%-76px)]'}
`

// padding+negative margin is required to hide a scrollbar
const TwTabsScrollContainer = tw.div`
  board-tabs-scroll-container
  w-fit
  max-w-full
  overflow-y-hidden
  overflow-x-scroll
  pb-24
  -mb-24
`

const TwTabsContainer = tw.div`
  board-tabs-container
  w-fit
  flex
  items-center
  gap-2
`

const TwControls = tw.div`
  relative
  flex-none
  flex
  items-center
`

// Custom scroll into view b/c HTMLElement#scrollIntoView doesn't take fade
// thresholds into account resulting in part of a tab being hidden by a fade
const scrollIntoView = (
  scrollable: HTMLElement | null | undefined,
  target: HTMLElement | null | undefined,
  threshold: number
): void => {
  if (scrollable && target) {
    const s = scrollable.getBoundingClientRect() // "scroll window"
    const t = target.getBoundingClientRect()
    const isTargetVisible =
      t.left > s.left + threshold && t.right < s.right - threshold
    const isTargetLarger = t.width > s.width
    if (!isTargetVisible) {
      let left: number
      if (isTargetLarger) {
        // target is larger than scroll window, prioritise right target border
        const srx = s.right - threshold
        const trx = t.right
        left = trx - srx
      } else {
        // target fits in scroll window, prioritise target center
        const tcx = t.left + t.width / 2
        const scx = s.left + s.width / 2
        left = tcx - scx
      }
      scrollable.scrollBy({left, behavior: 'auto'})
    }
  }
}

const useScrollBoardIntoView = (ref: React.RefObject<HTMLElement>): void => {
  const [state] = useBoardTabsStore()

  // setTimeout for scrolls to prevent mysterious synchronous issues w/ scrolling
  useEffect(() => {
    if (state.addingBoard) {
      setTimeout(() => {
        ref.current && (ref.current.scrollLeft = Number.MAX_SAFE_INTEGER)
      })
    }
  }, [state.addingBoard])

  useEffect(() => {
    setTimeout(() => {
      scrollIntoView(
        ref.current,
        ref.current?.querySelector(`[data-tab-id="${state.currentBoard?.id}"]`),
        fadeThreshold
      )
    })
  }, [state.currentBoard?.id])
}

const useShowScrollButtons = (
  ref: React.RefObject<HTMLElement>,
  innerRef: React.RefObject<HTMLElement>
): {showScroll: boolean; refreshScroll: () => void} => {
  const [showScroll, setShowScroll] = useState(false)

  const refreshScroll = useCallback(() => {
    if (ref.current && innerRef.current) {
      const {width} = ref.current.getBoundingClientRect()
      const {width: innerWidth} = innerRef.current.getBoundingClientRect()
      setShowScroll(innerWidth - width > 1)
    }
  }, [setShowScroll, ref, innerRef])

  return {showScroll, refreshScroll}
}

/*
 Scroll can be using touchpad or using mousewheel:
 - touchpad scroll on all OSs produces events with small deltaX/Y and can happen
   in any direction
 - mousewheel scroll produces
   - on windows -- fixed number on deltaY, deltaX=0
   - on mac -- variable number on deltaY depending on scrolling speed, deltaX=0

 We use:
 - Euclidean distance to combine deltas on both axes b/c we don't know if scroll
   happens using touchpad or using mouse
 - `sign` to find in what direction to scroll; element scrolls horizontally, so
   we first use sign of displacement by X, then displacement by Y if dx = 0
 - `requestAnimationFrame` to scroll with current framerate and not faster
 - `auto` scroll behaviour, b/c `smooth` scroll interacts poorly with another
   `smooth` scroll call at the next frame
 */
const useWheelScroll = (
  ref: React.RefObject<HTMLElement>
): ((e: React.WheelEvent) => void) => {
  const dxRef = useRef(0)
  const dyRef = useRef(0)
  const cbRef = useRef(0)
  return useCallback(
    e => {
      dxRef.current += e.deltaX
      dyRef.current += e.deltaY

      if (!cbRef.current) {
        cbRef.current = requestAnimationFrame(() => {
          const dx = dxRef.current
          const dy = dyRef.current
          let d = Math.sqrt(dx * dx + dy * dy)
          d = Math.sign(dx) ? Math.sign(dx) * d : Math.sign(dy) * d
          ref.current?.scrollBy(d, 0)
          dxRef.current = 0
          dyRef.current = 0
          cbRef.current = 0
        })
      }

      e.stopPropagation()
    },
    [ref, dxRef, dyRef, cbRef]
  )
}

const useKeyboardNavigation = () => {
  const [state] = useBoardTabsStore()

  useGlobalKeyDown({
    anyKey: ({keyCode, ctrl, meta}) => {
      const idx = keyCode - 49

      if (idx >= -1 && idx < 9 && (ctrl || meta)) {
        const realIndex = idx === -1 ? 9 : idx
        const boardId = state.boardsOrdered[realIndex]?.id
        if (boardId && Items.click(Items.get(boardId))) {
          const companyId = globals.getCompanyId()
          PageService.goToBoard(boardId, companyId)
          return true
        }
      }

      return false
    }
  })
}

const BoardTabsBase = observer(() => {
  const [state, store] = useBoardTabsStore()
  const [dndState] = useDnDStore()
  const wrapperRef = useRef<HTMLElement>(null)
  const ref = useRef<HTMLElement>(null)
  const innerRef = useRef<HTMLElement>(null)
  const refCollapsed = useRef<boolean>(false)

  useEffect(() => {
    refCollapsed.current = isCollapsedHeader()
  }, [state.currentBoard?.id])

  const {onDown, onUp} = useBoardTabsButtonScroll(ref)
  const refreshFade = useRefreshFade(ref, innerRef)
  const {showScroll, refreshScroll} = useShowScrollButtons(ref, innerRef)
  const onWheel = useWheelScroll(ref)

  // Fix tabs container width when dragging to prevent that container from
  // size change when on drag some element is removed and not yet added
  const [innerWidth, setInnerWidth] = useState(0)
  const innerStyle = useMemo(
    () => (dndState.dragStarted ? {width: innerWidth} : {}),
    [innerWidth, dndState.dragStarted]
  )

  useScrollBoardIntoView(ref)
  useResizeObserver({
    ref: wrapperRef,
    onResize() {
      refreshFade()
      refreshScroll()
    }
  })
  useResizeObserver({
    ref: innerRef,
    onResize() {
      innerRef.current &&
        setInnerWidth(innerRef.current.getBoundingClientRect().width)
      refreshFade()
      refreshScroll()
    }
  })
  useKeyboardNavigation()
  const canAddBoardToCurrentProject = useMemo(
    () => store.canAddBoardToCurrentProject(),
    [state.currentBoard?.location]
  )

  return (
    <TwBoardTabs ref={wrapperRef} $collapsed={refCollapsed.current}>
      <TwTabs onWheel={onWheel} $collapsed={refCollapsed.current}>
        <TwTabsScrollContainer ref={ref} onScroll={refreshFade}>
          <TwTabsContainer ref={innerRef} style={innerStyle}>
            {state.boardsOrdered.map(x => (
              <BoardTab key={x.id} board={x as Hub} />
            ))}
            {state.addingBoard && <NewBoardTab />}
          </TwTabsContainer>
        </TwTabsScrollContainer>
      </TwTabs>
      <TwControls>
        {canAddBoardToCurrentProject && (
          <IconButton
            data-test="add-new-board"
            icon="IconPlus"
            onClick={store.startBoardAdding}
            size="small"
            color="panelIconSecondary"
            className="!h-32 !w-32"
          />
        )}
        {showScroll && (
          <>
            <IconButton
              icon="IconChevronLeft"
              size="small"
              color="panelIconSecondary"
              onMouseDown={() => onDown('l')}
              onMouseUp={() => onUp('l')}
              className="!w-32 !h-32 ml-4"
            />
            <IconButton
              icon="IconChevronRight"
              size="small"
              color="panelIconSecondary"
              onMouseDown={() => onDown('r')}
              onMouseUp={() => onUp('r')}
              className="!w-32 !h-32 ml-2"
            />
          </>
        )}
      </TwControls>
    </TwBoardTabs>
  )
})

export const BoardTabs = () => {
  const [store] = useState(() => new BoardTabsStore(boardSettingsStore))

  const project = PageService.getCurrentProject()
  useSyncEvent(
    'current',
    {filter: project?.id},
    () => project && store.syncOnUpdate(),
    [project?.id]
  )

  useEffect(() => {
    const id = setInterval(store.syncRegularly, 1000)
    return () => clearInterval(id)
  }, [store])

  useLocationUpdateEffect(() => {
    project && store.syncOnUpdate()
    return true
  }, [project?.id])

  if (!project) {
    return null
  }

  return (
    <BoardTabsContext.Provider value={store}>
      <BoardTabsBase />
    </BoardTabsContext.Provider>
  )
}
